import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Check, X, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../../lib/apiError';

const STATUS_STYLE = {
  accepted: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  rejected: 'bg-rose-100 text-rose-700 border-rose-300',
};

const JoinRequestsDialog = ({ booking, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!booking) return;
    try {
      const res = await axios.get(`${API}/sessions/${booking.id}/join-requests`);
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e, 'Failed to load requests'));
    }
  }, [booking]);

  useEffect(() => { setData(null); load(); }, [load]);

  const decide = async (req, action) => {
    setBusy(req.id);
    try {
      await axios.post(`${API}/sessions/join-requests/${req.id}/decision`, { action });
      toast.success(action === 'accept' ? `${req.student_name} has joined your session` : 'Request rejected');
      await load();
      onChanged();
    } catch (e) {
      toast.error(apiError(e, 'Could not update request'));
    } finally {
      setBusy(null);
    }
  };

  const pending = (data?.requests || []).filter((r) => r.status === 'pending');
  const handled = (data?.requests || []).filter((r) => r.status !== 'pending');

  return (
    <Dialog open={!!booking} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="join-requests-dialog">
        <DialogHeader>
          <DialogTitle>Join requests</DialogTitle>
          <DialogDescription>
            Accepting gives the student access to your session. It doesn't change what you paid and no payment is collected.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading...</p>
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto">
            <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 text-sm" data-testid="join-requests-capacity">
              <span className="font-semibold text-violet-800">{data.filled} of {data.group_size} places filled</span>
              <span className="text-slate-600"> · {data.spots_left} open</span>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge variant="outline" className="bg-white">You (host)</Badge>
                {data.participants.map((p) => (
                  <Badge key={p.student_id} variant="outline" className="bg-white"><UserCheck className="w-3 h-3 mr-1" />{p.name}</Badge>
                ))}
              </div>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-800 mb-2">Pending ({pending.length})</p>
              {pending.length === 0 && <p className="text-sm text-slate-500" data-testid="no-pending-requests">No pending requests.</p>}
              <div className="space-y-2">
                {pending.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3" data-testid={`join-request-${r.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{r.student_name}</p>
                      {r.message && <p className="text-xs text-slate-500 break-words">“{r.message}”</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" disabled={busy === r.id || data.spots_left === 0} onClick={() => decide(r, 'accept')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid={`accept-request-${r.id}`}>
                        <Check className="w-4 h-4 mr-1" />Accept
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === r.id} onClick={() => decide(r, 'reject')}
                        className="rounded-full border-rose-300 text-rose-600" data-testid={`reject-request-${r.id}`}>
                        <X className="w-4 h-4 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {handled.length > 0 && (
              <div className="pt-2">
                <p className="text-sm font-semibold text-slate-800 mb-2">Handled</p>
                <div className="space-y-1.5">
                  {handled.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`handled-request-${r.id}`}>
                      <span className="text-slate-700">{r.student_name}</span>
                      <Badge className={STATUS_STYLE[r.status] || ''}>{r.decision_note || r.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JoinRequestsDialog;
